import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArticleModal } from './ArticleModal';
import '../styles/BlogPage.css';

const API_BASE = '/admin/blogs/api/get-posts.php';

export const BlogPage = () => {
  const { slug: urlSlug } = useParams();
  const navigate = useNavigate();
  
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);

  const postsPerPage = 6;

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Handle URL slug changes
  useEffect(() => {
    if (urlSlug) {
      setSelectedPost(urlSlug);
    } else {
      setSelectedPost(null);
    }
  }, [urlSlug]);

  // Fetch blog posts
  const fetchPosts = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const res = await fetch(API_BASE);
      if (!res.ok) throw new Error('Failed to fetch posts');
      const data = await res.json();
      setPosts(data);

      const featuredRes = await fetch(`${API_BASE}?featured=true&limit=5`);
      if (featuredRes.ok) {
        const featuredData = await featuredRes.json();
        setFeaturedPosts(featuredData);
      }
    } catch (err) {
      console.error('Blog fetch error:', err);
      setError('Failed to load blog posts. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
    setCurrentPage(1);
  }, []);

  const handlePostClick = (post, e) => {
    e.preventDefault();
    setSelectedPost(post.slug);
    navigate(`/blogs/${post.slug}`);
  };

  const handleCloseModal = () => {
    setSelectedPost(null);
    navigate('/blogs');
  };

  // Pagination
  const indexOfLast = currentPage * postsPerPage;
  const indexOfFirst = indexOfLast - postsPerPage;
  const currentPosts = posts.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(posts.length / postsPerPage);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
    <div className="pageContainer">
      {/* Article Modal for Blog Posts */}
      <ArticleModal 
        isOpen={!!selectedPost}
        onClose={handleCloseModal}
        articleSlug={selectedPost}
        type="blogs"
      />

      {/* Header - Matching News Header Style */}
      <section className="news-page-header">
        <h1>Blogs</h1>
        <p>Read all the latest blog posts from Utter Radio.</p>

        <button
          className="btn btn-refresh"
          onClick={fetchPosts}
          disabled={isLoading}
        >
          <i className={`fas ${isLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
          {isLoading ? ' Loading...' : ' Refresh'}
        </button>
      </section>

      {/* Loading State */}
      {isLoading && (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading blog posts…</p>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="error-state" style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'linear-gradient(140deg,rgba(190, 7, 64, 1) 0%, rgba(153, 5, 52, 1) 50%, rgba(237, 55, 112, 1) 100%)',
          margin: '20px 0'
        }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#fff', marginBottom: '20px' }}></i>
          <p style={{ color: '#fff', fontSize: '18px', marginBottom: '20px' }}>{error}</p>
          <button 
            className="btn btn-refresh" 
            onClick={fetchPosts}
            style={{
              padding: '12px 24px',
              background: '#fff',
              color: '#333',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            <i className="fas fa-sync-alt"></i> Try Again
          </button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && posts.length === 0 && (
        <div className="empty-state" style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: '#fff',
          margin: '20px 0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <i className="fas fa-pen-fancy" style={{ fontSize: '64px', color: '#626465', marginBottom: '20px' }}></i>
          <h3 style={{ color: '#374151', marginBottom: '10px', fontSize: '24px' }}>
            No Blog Posts Found
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '16px' }}>
            There are no blog posts available at the moment. Check back soon for new content!
          </p>
          <button 
            className="btn btn-refresh" 
            onClick={fetchPosts}
            style={{
              padding: '12px 24px',
              background: '#BD003A',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
        </div>
      )}

      {/* Blog Posts Grid */}
      {!isLoading && !error && posts.length > 0 && (
        <div className="blog-layout">
          {/* Featured Posts Sidebar */}
          {featuredPosts.length > 0 && (
            <aside className="news-sidebar">
              <h3><i className="fas fa-star" /> Featured Posts</h3>
              <ul className="trending-news">
                {featuredPosts.map((post, i) => (
                  <li key={post.id}>
                    <a 
                      href={`/blogs/${post.slug}`}
                      onClick={(e) => handlePostClick(post, e)}
                    >
                      <span>{i + 1}</span>
                      {post.title}
                    </a>
                  </li>
                ))}
              </ul>
            </aside>
          )}

          {/* Main Posts Grid */}
          <div className="blog-posts-grid">
            {currentPosts.map(post => (
              <article key={post.id} className="news-article-card">
                <img src={post.image} alt={post.title} />
                <div className="article-content">
                  {post.tags && post.tags.length > 0 && (
                    <div className="post-tags">
                      {post.tags.map(tag => (
                        <span key={tag} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                  <h3>{post.title}</h3>
                  <p>{post.excerpt}</p>
                  <div className="post-meta">
                    <span><i className="fas fa-calendar"></i> {formatDate(post.date)}</span>
                    <span><i className="fas fa-clock"></i> {post.read_time} min read</span>
                  </div>
                  <a
                    href={`/blogs/${post.slug}`}
                    onClick={(e) => handlePostClick(post, e)}
                    className="btn btn-outline"
                  >
                    Read More
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            disabled={currentPage === 1}
            onClick={() => goToPage(currentPage - 1)}
          >
            ‹ Prev
          </button>

          {[...Array(totalPages)].map((_, i) => {
            const page = i + 1;
            return (
              <button
                key={page}
                className={`page-btn ${page === currentPage ? 'active' : ''}`}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            );
          })}

          <button
            className="page-btn"
            disabled={currentPage === totalPages}
            onClick={() => goToPage(currentPage + 1)}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
    </>
  );
};